import { FormControl, FormGroup, FormLabel } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { memo } from 'react';
import { CartModifierGroup } from '../../../utils/cart';
import {
  checkItemInCartItemTree,
  isDefaultItemInModGroup,
  isItem86edToday,
} from '../../../utils/menu';
import { ModGroupLabel } from '../components/ModGroupLabel';
import MultipleModChoice from '../components/MultipleModChoice';
import RecursiveModGroup from '../menu/RecursiveModGroup';
import { getModGroupItemLabel, sortAndFilterMenuItems } from '../util/helper';
import { IModGroup } from './modGroup.props';
import { canShowItem, hasModGroupChildren } from './modGroup.utils';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import Colors from '../../../utils/color';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import { useSelector } from 'react-redux';

const useStyles = makeStyles<{
  hasChildren: boolean;
  isHighlighted: boolean;
  isLabelClickable: boolean;
}>()(({ spacing }, { hasChildren, isHighlighted, isLabelClickable }) => {
  const bgColor = isHighlighted ? Colors.alto : Colors.white;
  const mBottom = hasChildren ? spacing(1.25) : 0;
  return {
    modGroup: {
      margin: '0 !important',
      '& legend': {
        width: '100%',
      },
      width: '100%',
      backgroundColor: bgColor,
    },
    modGroupContainer: {
      marginBottom: mBottom,
    },
    modGroupLabel: {
      cursor: isLabelClickable ? 'pointer' : 'default',
    },
  };
});

const MultipleSelectionModGroup = (props: IModGroup) => {
  const {
    modGroup,
    onChange,
    cartItem,
    pathToItem,
    parentModifier,
    isHighlighted,
    isSelectedToFocus,
    sendModGroupTTSMessage,
  } = props;
  const isBrandMenu = getMenuUnificationFeatureFlag();
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);

  //sort and filter the modifiers before rendering
  const menuItems = isBrandMenu
    ? Object.values(modGroup.menuItems)
    : sortAndFilterMenuItems(modGroup.menuItems);

  const currentItem = checkItemInCartItemTree({
    cartItem,
    pathToItem,
  }) as CartModifierGroup;
  let selectedModNumber = 0;
  if (currentItem) {
    const selectedMod = currentItem?.selectedItems || [];
    selectedModNumber = Object.values(selectedMod).length;
  }
  const key = [cartItem.id, modGroup.id].join('_');
  const hasChildren = hasModGroupChildren(props);
  const isLabelClickable = !!(isBrandMenu && modGroup.description);
  const { classes } = useStyles({
    hasChildren,
    isHighlighted: !!isHighlighted || !!isSelectedToFocus,
    isLabelClickable,
  });

  const handleLabelClick = () => {
    if (isLabelClickable) {
      sendModGroupTTSMessage(modGroup);
    }
  };

  return (
    <div key={key} className={classes.modGroupContainer}>
      <FormControl
        component="fieldset"
        className={classes.modGroup}
        data-testid="mod-group-form"
      >
        <div onClick={handleLabelClick}>
          <FormLabel className={classes.modGroupLabel}>
            <ModGroupLabel
              modGroup={modGroup}
              selectedModNumber={selectedModNumber}
              parentModifier={parentModifier}
            />
          </FormLabel>
        </div>
        <FormGroup row>
          {menuItems.map((child, index) => {
            const is86edToday = isItem86edToday(child);
            const pathToModChoice = [pathToItem, child.id].join('__');
            const isSelected = !!checkItemInCartItemTree({
              cartItem,
              pathToItem: pathToModChoice,
            });
            const id = child.itemId;
            const name = child.name;
            const modLabel = getModGroupItemLabel(child);

            const isDefaultItem = isDefaultItemInModGroup(id, modGroup);

            const isItemUnavailableNow = isBrandMenu
              ? isBMModifierAnUnavailableConstraint(
                  cartItem,
                  unavailableItemsByName,
                  parentModifier,
                  pathToItem,
                  modGroup.name,
                  child.name
                )
              : false;

            const showItem = canShowItem({
              isBrandMenu,
              unavailableItemsByName,
              itemName: name,
              cartItem,
              modGroupName: modGroup.name,
              childAvailability: child?.available,
              is86edToday,
            });

            if (showItem) {
              return (
                <MultipleModChoice
                  key={pathToModChoice}
                  id={id}
                  name={name}
                  modLabel={modLabel}
                  onChange={onChange(modGroup)}
                  is86edToday={
                    isBrandMenu
                      ? !child.available || is86edToday || isItemUnavailableNow
                      : is86edToday
                  }
                  isSelected={isSelected}
                  isDefault={isDefaultItem}
                />
              );
            }
            return null;
          })}
        </FormGroup>
      </FormControl>
      <RecursiveModGroup {...props} />
    </div>
  );
};

export default memo(MultipleSelectionModGroup);
